import React from 'react';
import {
  Circle,
  Marker,
  Tooltip,
  MapContainer,
  TileLayer,
  useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import type { LatLngBoundsExpression, PointExpression } from 'leaflet';
import { Icon } from 'leaflet';
import './index.scss';
import Heatmap from './Heatmap';
import ImageDownloader from './ImageDownloader';
import Alert from './Alert';
import MapUpdater from './MapUpdater';
import PolygonLayer from './PolygonLayer';
import MapClickHandler from './MapClickHandler';
import MapTapHandler from './MapTapHandler';
import RecenterControl from './RecenterControl';
import MapPanes from './MapPanes';

export interface InteractiveMapCircle {
  center: [number, number];
  radius: number;
  pathOptions?: {
    color?: string;
    fillColor?: string;
  };
}

export interface InteractiveMapMarkerTooltipExtra {
  label: string;
  value: string;
  unit?: string;
}

export interface InteractiveMapMarker {
  groupId?: number;
  locationId?: number;
  position: [number, number];
  label: string;
  onClick?: () => void;
  tooltipExtra?: InteractiveMapMarkerTooltipExtra;
}

const brazilBoundaries: [number, number][] = [
  [-33.7682, -73.9845],
  [5.2719, -34.7922],
];

const DEFAULT_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Tile satelite Esri (sem labels/rios/cidades, alinhado ao novo padrao visual
// dos mapas do Agro). Usado em combinacao com BrazilStatesLayer.
export const ESRI_SATELLITE_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const ESRI_SATELLITE_TILE_ATTRIBUTION = 'Tiles &copy; Esri';

export default function InteractiveMap({
  children,
  bounds = brazilBoundaries,
  circles = [],
  markers = [],
  minZoom = 4,
  maxZoom = 8,
  center,
  zoom,
  dragging = true,
  zoomControl = true,
  boundsPadding = [20, 20],
  maxBounds,
  maxBoundsViscosity = 0,
  scrollWheelZoom = true,
  doubleClickZoom = true,
  touchZoom = true,
  boxZoom = true,
  keyboard = true,
  tileUrl = DEFAULT_TILE_URL,
  tileAttribution = DEFAULT_TILE_ATTRIBUTION,
  overlayUrl,
  onMarkerEnter,
  onMarkerLeave,
  preferCanvas = false,
  recenterControl = true,
}: {
  children?: React.ReactNode;
  bounds?: [number, number][];
  circles?: InteractiveMapCircle[];
  markers?: InteractiveMapMarker[];
  minZoom?: number;
  maxZoom?: number;
  center?: [number, number];
  zoom?: number;
  dragging?: boolean;
  zoomControl?: boolean;
  /** Padding do fitBounds (responsivo por tela, se quiser). */
  boundsPadding?: PointExpression;
  /** Limita a câmera para não "vazar" pro oceano. */
  maxBounds?: LatLngBoundsExpression;
  maxBoundsViscosity?: number;
  /** Travar/permitir interações do mapa. */
  scrollWheelZoom?: boolean;
  doubleClickZoom?: boolean;
  touchZoom?: boolean;
  boxZoom?: boolean;
  keyboard?: boolean;
  /** Tile base (default: OpenStreetMap). Para satelite usar tile da Esri. */
  tileUrl?: string;
  tileAttribution?: string;
  /** Tile opcional desenhado por cima do tile base. */
  overlayUrl?: string;
  /** Disparado ao entrar/sair de um marker — útil para suprimir tooltips de mapa. */
  onMarkerEnter?: () => void;
  onMarkerLeave?: () => void;
  /** Renderiza vetores (Circle/Polyline) em canvas — necessário para milhares de shapes. */
  preferCanvas?: boolean;
  /** Botão que devolve o mapa ao enquadramento inicial. Desligar só em mapa sem pan/zoom. */
  recenterControl?: boolean;
}) {
  const useCenterZoom = center != null && zoom != null;

  return (
    <MapContainer
      center={useCenterZoom ? center : undefined}
      zoom={useCenterZoom ? zoom : undefined}
      bounds={useCenterZoom ? undefined : bounds}
      boundsOptions={useCenterZoom ? undefined : { padding: boundsPadding }}
      minZoom={minZoom}
      maxZoom={maxZoom}
      dragging={dragging}
      zoomControl={zoomControl}
      maxBounds={maxBounds}
      maxBoundsViscosity={maxBoundsViscosity}
      scrollWheelZoom={scrollWheelZoom}
      doubleClickZoom={doubleClickZoom}
      touchZoom={touchZoom}
      boxZoom={boxZoom}
      keyboard={keyboard}
      preferCanvas={preferCanvas}
      className="map-container"
    >
      {/* Primeiro filho de proposito: cria os panes que ordenam mancha, divisas
          e alerta antes de qualquer camada montar. */}
      <MapPanes />
      {circles.map((circle) => (
        <Circle
          key={`${circle.center}-${circle.radius}`}
          center={circle.center}
          radius={circle.radius}
          pathOptions={circle.pathOptions}
        />
      ))}
      {markers.map((marker) => (
        <Marker
          key={`${marker.position}-${marker.label}`}
          position={marker.position}
          icon={new Icon({ iconUrl: markerIconPng, iconSize: [25, 41], iconAnchor: [12, 41] })}
          eventHandlers={{
            ...(marker.onClick != null && { click: marker.onClick }),
            ...(onMarkerEnter != null && { mouseover: onMarkerEnter }),
            ...(onMarkerLeave != null && { mouseout: onMarkerLeave }),
          }}
        >
          <Tooltip direction="bottom">
            <span className="imap-tooltip-label">{marker.label}</span>
            {marker.tooltipExtra && (
              <>
                <span className="imap-tooltip-extra-label">{marker.tooltipExtra.label}</span>
                <span className="imap-tooltip-extra-value">
                  {marker.tooltipExtra.value}
                  {marker.tooltipExtra.unit && (
                    <span className="imap-tooltip-extra-unit">{` ${marker.tooltipExtra.unit}`}</span>
                  )}
                </span>
              </>
            )}
          </Tooltip>
        </Marker>
      ))}
      <TileLayer
        attribution={tileAttribution}
        url={tileUrl}
      />
      {overlayUrl && <TileLayer url={overlayUrl} />}
      {recenterControl && (
        <RecenterControl
          bounds={bounds}
          center={center}
          zoom={zoom}
          boundsPadding={boundsPadding}
        />
      )}
      {children}
    </MapContainer>
  );
}

function MapHoverTracker({
  onMove,
  onLeave,
}: {
  onMove: (lat: number, lng: number, x: number, y: number) => void;
  onLeave: () => void;
}) {
  useMapEvents({
    mousemove(e) {
      onMove(e.latlng.lat, e.latlng.lng, e.containerPoint.x, e.containerPoint.y);
    },
    mouseout() {
      onLeave();
    },
  });
  return null;
}

InteractiveMap.Heatmap = Heatmap;
InteractiveMap.ImageDownloader = ImageDownloader;
InteractiveMap.Alert = Alert;
InteractiveMap.MapUpdater = MapUpdater;
InteractiveMap.PolygonLayer = PolygonLayer;
InteractiveMap.MapClickHandler = MapClickHandler;
InteractiveMap.MapTapHandler = MapTapHandler;
InteractiveMap.MapHoverTracker = MapHoverTracker;
